'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CountBadge } from '@statura/ui';
import { navigationRowClassName } from './navigation-row';
import { trackNavV2Event, type NavV2EventSource } from '@/lib/nav-v2/telemetry';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GripVertical, Pin, PinOff, Search } from 'lucide-react';
import {
  flattenNavV2,
  type FlatNavV2Item,
  type NavBadgeCounts,
  type NavV2Group,
} from '@/lib/navigation/nav-v2';
import { NavV2ContextMenu, type ContextMenuPosition } from './context-menu';

const MAX_VISIBLE_PINS = 8;

/**
 * Reorder the visible pin subset while preserving non-visible pins at
 * their original positions in the full persisted array.
 *
 * Bugbot Phase 2 finding: drag-end on the visible-pin subset used to
 * replace the entire persisted `pinnedHrefs` array with the visible
 * subset, silently dropping every pin the user couldn't currently see
 * (route lost module access, role gate temporarily denies, etc).
 *
 * Correct semantics: visible pins reflect the drag; non-visible pins
 * stay anchored at their original positions. We walk the original
 * `pinnedHrefs` and, for every position whose href is in the visible
 * subset, take the next href from the reordered visible queue.
 *
 * Exported (rather than inlined) so the unit suite can lock the
 * preservation invariant.
 */
export function reorderVisiblePreservingHidden(
  pinnedHrefs: readonly string[],
  visibleHrefsInOrder: readonly string[],
  oldVisibleIdx: number,
  newVisibleIdx: number,
): string[] {
  if (oldVisibleIdx === newVisibleIdx) return [...pinnedHrefs];
  if (oldVisibleIdx < 0 || newVisibleIdx < 0) return [...pinnedHrefs];
  if (oldVisibleIdx >= visibleHrefsInOrder.length || newVisibleIdx >= visibleHrefsInOrder.length) {
    return [...pinnedHrefs];
  }
  const newVisibleOrder: string[] = arrayMove(
    [...visibleHrefsInOrder],
    oldVisibleIdx,
    newVisibleIdx,
  );
  const visibleSet = new Set(visibleHrefsInOrder);
  let cursor = 0;
  return pinnedHrefs.map((href) => {
    if (visibleSet.has(href)) {
      // Defensive ?? href — the cursor will always be in range because we
      // walk pinnedHrefs in order and increment only for visible matches,
      // which are exactly the elements of newVisibleOrder.
      const next = newVisibleOrder[cursor] ?? href;
      cursor += 1;
      return next;
    }
    return href;
  });
}

interface Props {
  /** Plan-specific item metadata, already filtered by the sidebar. */
  groups?: readonly NavV2Group[];
  /** Ordered list of pinned hrefs (live state — the parent owns this). */
  pinnedHrefs: readonly string[];
  /** Set of hrefs currently visible to the user under their role × program × RBAC × module gates. */
  visibleHrefs: ReadonlySet<string>;
  /** Current pathname for active-state highlighting. */
  currentPath: string;
  /** Reorder callback — parent persists via useUserPreferences. */
  onReorder: (next: string[]) => void;
  /** Toggle (add/remove) pin — parent persists. */
  onTogglePin: (href: string) => void;
  /** Trigger Cmd-K from the empty-state CTA. */
  onCmdK: () => void;
  /** Real, already-scoped nav badge counts keyed by NavBadgeKey. */
  badgeCounts?: NavBadgeCounts;
  /**
   * Phase 6 telemetry source override. Defaults to `'sidebar-pinned'`;
   * `NavV2Sidebar` overrides to `'mobile-drawer'` when rendered inside
   * `NavV2MobileDrawer` so the per-surface event attribution is correct.
   */
  clickSource?: NavV2EventSource;
}

/**
 * Pinned-items list.
 * - Drag to reorder via `@dnd-kit/sortable` (mouse + keyboard via Tab/arrow).
 * - Right-click any row for context menu (pin/unpin/copy link).
 * - Empty state guides the user to pin via Cmd-K or right-click.
 * - Filters out pins whose href is no longer visible to the user
 *   (e.g., role lost access, module disabled) — never breaks the sidebar.
 * - Shows top N pins; "show all" reveals the rest.
 */
export function PinnedList({
  groups,
  pinnedHrefs,
  visibleHrefs,
  currentPath,
  onReorder,
  onTogglePin,
  onCmdK,
  badgeCounts,
  clickSource = 'sidebar-pinned',
}: Props) {
  const router = useRouter();
  const [showAll, setShowAll] = useState(false);
  const [menu, setMenu] = useState<ContextMenuPosition | null>(null);
  // Phase 1 runtime fix: @dnd-kit's DndContext schedules layout work during the
  // insertion-effect phase. Defer drag-and-drop chrome until after first paint so
  // preference hydration + pin migration don't trip React's update guard.
  const [dndReady, setDndReady] = useState(false);
  useEffect(() => {
    // Double-rAF: wait until preference hydration + pin migration finish
    // before @dnd-kit mounts (Phase 1 insertion-effect guard).
    let outer = 0;
    const inner = requestAnimationFrame(() => {
      outer = requestAnimationFrame(() => {
        setDndReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(inner);
      cancelAnimationFrame(outer);
    };
  }, []);

  // Resolve pins from the same plan-specific items the sidebar displays.
  const itemLookup = useMemo<Map<string, FlatNavV2Item>>(() => {
    const m = new Map<string, FlatNavV2Item>();
    for (const it of flattenNavV2(groups)) m.set(it.href, it);
    return m;
  }, [groups]);

  // Resolve pinned hrefs to items, preserving user-set order and dropping
  // any href whose item is no longer visible / known.
  const resolved = useMemo(() => {
    return pinnedHrefs
      .map((href) => itemLookup.get(href))
      .filter((it): it is FlatNavV2Item => !!it && visibleHrefs.has(it.href));
  }, [pinnedHrefs, itemLookup, visibleHrefs]);

  const visible = showAll ? resolved : resolved.slice(0, MAX_VISIBLE_PINS);
  const hiddenCount = Math.max(0, resolved.length - MAX_VISIBLE_PINS);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = resolved.findIndex((i) => i.href === active.id);
    const newIndex = resolved.findIndex((i) => i.href === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    onReorder(
      reorderVisiblePreservingHidden(
        pinnedHrefs,
        resolved.map((i) => i.href),
        oldIndex,
        newIndex,
      ),
    );
  };

  if (resolved.length === 0) {
    return (
      <div className="rounded-overlay border border-dashed border-control-border bg-surface-sheet/60 p-4 text-center">
        <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-control bg-selected">
          <Pin className="h-3.5 w-3.5 text-selected-foreground" aria-hidden="true" />
        </div>
        <p className="text-caption font-semibold text-text-heading">No pinned items yet</p>
        <p className="mt-1 text-caption leading-snug text-text-muted">
          Right-click any nav item or open command palette to pin it here.
        </p>
        <button
          type="button"
          onClick={onCmdK}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-control border border-control-border/30 bg-surface-sheet px-2.5 py-1 text-caption text-selected-foreground hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <Search className="h-3 w-3" aria-hidden="true" />
          Find one
        </button>
      </div>
    );
  }

  // Phase 2 retro audit P3 (codex pass 2): when both a parent and a
  // child route are pinned (e.g. /medications and /medications/mar) and
  // the user is on /medications/mar, evaluating the prefix test per row
  // marked BOTH rows active and exposed `aria-current="page"` twice. Pick
  // the single longest-prefix winner instead — the one most specific to
  // the current path.
  const activeHref: string | null = (() => {
    let best: { href: string; length: number } | null = null;
    for (const item of visible) {
      const matches = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
      if (!matches) continue;
      if (!best || item.href.length > best.length) {
        best = { href: item.href, length: item.href.length };
      }
    }
    return best?.href ?? null;
  })();

  const rowProps = (item: FlatNavV2Item) => ({
    item,
    isActive: item.href === activeHref,
    clickSource,
    badgeCount: item.badgeKey ? badgeCounts?.[item.badgeKey] : undefined,
    onUnpin: () => onTogglePin(item.href),
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      setMenu({
        x: e.clientX,
        y: e.clientY,
        target: { href: item.href, label: item.label, groupTitle: item.groupTitle },
      });
    },
  });

  return (
    <div>
      {dndReady ? (
        <PinnedListDndShell visible={visible} onDragEnd={handleDragEnd} rowProps={rowProps} />
      ) : (
        <ul className="flex flex-col gap-px" role="list" aria-label="Pinned navigation items">
          {visible.map((item) => (
            <StaticPinnedRow key={item.href} {...rowProps(item)} />
          ))}
        </ul>
      )}

      {hiddenCount > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1 flex w-full items-center justify-center gap-1 rounded-control px-2.5 py-1.5 text-caption text-selected-foreground hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          Show {hiddenCount} more
        </button>
      )}
      {showAll && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-1 flex w-full items-center justify-center gap-1 rounded-control px-2.5 py-1.5 text-caption text-selected-foreground hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          Show fewer
        </button>
      )}

      <NavV2ContextMenu
        position={menu}
        isPinned={menu ? pinnedHrefs.includes(menu.target.href) : false}
        onTogglePin={onTogglePin}
        onOpen={(href) => {
          // Codex Phase 6 pass 4 P3: count context-menu "Open" actions
          // as item clicks too — otherwise right-click → Open is
          // invisible to telemetry while the direct Link click is not.
          trackNavV2Event('nav_v2_item_clicked', {
            source: clickSource,
            route: href,
            variant: 'context-menu',
          });
          router.push(href);
        }}
        onClose={() => setMenu(null)}
      />
    </div>
  );
}

interface RowProps {
  item: FlatNavV2Item;
  isActive: boolean;
  onUnpin: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  clickSource: NavV2EventSource;
  badgeCount?: number;
}

/**
 * Mount @dnd-kit only after the parent defers first paint. Keeps useSensors /
 * DndContext out of the insertion-effect phase while preferences hydrate.
 */
function PinnedListDndShell({
  visible,
  onDragEnd,
  rowProps,
}: {
  visible: FlatNavV2Item[];
  onDragEnd: (event: DragEndEvent) => void;
  rowProps: (item: FlatNavV2Item) => RowProps;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={visible.map((i) => i.href)} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col gap-px" role="list" aria-label="Pinned navigation items">
          {visible.map((item) => (
            <SortablePinnedRow key={item.href} {...rowProps(item)} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function StaticPinnedRow({
  item,
  isActive,
  onUnpin,
  onContextMenu,
  clickSource,
  badgeCount,
}: RowProps) {
  const Icon = item.icon;

  return (
    <li
      onContextMenu={onContextMenu}
      className={[
        `group relative ${navigationRowClassName} pl-0 pr-1.5`,
        isActive ? 'bg-selected' : 'hover:bg-surface-soft',
      ].join(' ')}
      data-nav-v2-pin={item.href}
    >
      <span className="h-9 w-8 shrink-0" aria-hidden="true" />
      <Link
        href={item.href}
        aria-current={isActive ? 'page' : undefined}
        onClick={() =>
          trackNavV2Event('nav_v2_item_clicked', {
            source: clickSource,
            route: item.href,
          })
        }
        className={[
          'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-control px-1.5 text-body focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          isActive ? 'font-semibold text-selected-foreground' : 'font-medium text-text-muted',
        ].join(' ')}
      >
        <Icon
          className={[
            'h-3.5 w-3.5',
            isActive ? 'text-selected-foreground' : 'text-text-muted',
          ].join(' ')}
          aria-hidden="true"
        />
        <span className="truncate">{item.label}</span>
        {typeof badgeCount === 'number' && badgeCount > 0 && (
          <CountBadge
            compact
            count={badgeCount}
            label="items need attention"
            className="self-center ml-auto bg-status-warning-bg text-status-warning-fg"
          />
        )}
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUnpin();
        }}
        aria-label={`Unpin ${item.label}`}
        className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:bg-surface-soft hover:text-text-body focus:outline-none focus-visible:ring-2 focus-visible:ring-focus opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-lg:opacity-100"
      >
        <PinOff className="h-3 w-3" aria-hidden="true" />
      </button>
    </li>
  );
}

function SortablePinnedRow({
  item,
  isActive,
  onUnpin,
  onContextMenu,
  clickSource,
  badgeCount,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.href,
  });
  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) scaleX(${transform.scaleX}) scaleY(${transform.scaleY})`
      : undefined,
    transition,
  };
  const Icon = item.icon;

  return (
    <li
      ref={setNodeRef}
      style={style}
      onContextMenu={onContextMenu}
      className={[
        `group relative ${navigationRowClassName} pl-0 pr-1.5`,
        isActive ? 'bg-selected' : 'hover:bg-surface-soft',
        isDragging ? 'opacity-50' : '',
      ].join(' ')}
      data-nav-v2-pin={item.href}
    >
      {/* Drag handle (mouse + keyboard via dnd-kit attributes/listeners) */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${item.label}`}
        className="flex h-9 w-8 cursor-grab items-center justify-center text-text-muted opacity-0 transition-opacity group-hover:opacity-100 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-focus active:cursor-grabbing"
      >
        <GripVertical className="h-3 w-3" aria-hidden="true" />
      </button>

      {/* Nav link */}
      <Link
        href={item.href}
        aria-current={isActive ? 'page' : undefined}
        onClick={() =>
          trackNavV2Event('nav_v2_item_clicked', {
            source: clickSource,
            route: item.href,
          })
        }
        className={[
          'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-control px-1.5 text-body focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          isActive ? 'font-semibold text-selected-foreground' : 'font-medium text-text-muted',
        ].join(' ')}
      >
        <Icon
          className={[
            'h-3.5 w-3.5',
            isActive ? 'text-selected-foreground' : 'text-text-muted',
          ].join(' ')}
          aria-hidden="true"
        />
        <span className="truncate">{item.label}</span>
        {typeof badgeCount === 'number' && badgeCount > 0 && (
          <CountBadge
            compact
            count={badgeCount}
            label="items need attention"
            className="self-center ml-auto bg-status-warning-bg text-status-warning-fg"
          />
        )}
      </Link>

      {/* Hover-only unpin */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUnpin();
        }}
        aria-label={`Unpin ${item.label}`}
        className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:bg-surface-soft hover:text-text-body focus:outline-none focus-visible:ring-2 focus-visible:ring-focus opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-lg:opacity-100"
      >
        <PinOff className="h-3 w-3" aria-hidden="true" />
      </button>
    </li>
  );
}
